import { useState } from 'react'
import { Plane, Clock, Mountain, Camera, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDiscoveryContext } from '../DiscoveryWizard'
import { altitudeOptions, cameraOptions } from '@/lib/discovery-data'

export function MissionProfileStep() {
  const { state, updateMissionProfile } = useDiscoveryContext()
  const { missionProfile } = state

  return (
    <div className="h-full flex flex-col items-center justify-center px-6 py-12 overflow-auto">
      <div className="max-w-2xl w-full space-y-8">
        {/* Header */}
        <div className="text-center wizard-fade-in">
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-3">
            Tell us about your operations
          </h2>
          <p className="text-muted-foreground">
            Help us understand your fleet and mission requirements
          </p>
        </div>

        {/* Input fields */}
        <div className="space-y-8">
          {/* Fleet Size */}
          <TypeformInput
            icon={<Plane className="w-5 h-5" />}
            label="How many drones are in your fleet?"
            sublabel="Number of platforms to equip with Cyclops"
            value={missionProfile.fleetSize}
            onChange={(val) => updateMissionProfile({ fleetSize: val })}
            suffix="drones"
            delay={0}
          />

          {/* Monthly Flight Hours */}
          <TypeformInput
            icon={<Clock className="w-5 h-5" />}
            label="Average monthly flight hours across fleet"
            sublabel="Total hours flown per month"
            value={missionProfile.monthlyFlightHours}
            onChange={(val) => updateMissionProfile({ monthlyFlightHours: val })}
            suffix="hours/month"
            delay={100}
          />

          {/* Operational Area */}
          <TypeformInput
            icon={<MapPin className="w-5 h-5" />}
            label="Operational area coverage needed"
            sublabel="Total area where you need map coverage"
            value={missionProfile.operationalAreaKm2}
            onChange={(val) => updateMissionProfile({ operationalAreaKm2: val })}
            suffix="km²"
            delay={200}
          />

          {/* Altitude Requirement */}
          <TypeformSelect
            icon={<Mountain className="w-5 h-5" />}
            label="Altitude requirements"
            sublabel="Maximum operating altitude"
            options={altitudeOptions}
            value={missionProfile.altitudeRequirement}
            onChange={(val) =>
              updateMissionProfile({
                altitudeRequirement: val as 'standard' | 'high_2km' | 'high_6km',
              })
            }
            delay={300}
          />

          {/* Camera Requirement */}
          <TypeformSelect
            icon={<Camera className="w-5 h-5" />}
            label="Camera payload"
            sublabel="Primary imaging sensor type"
            options={cameraOptions}
            value={missionProfile.cameraRequirement}
            onChange={(val) =>
              updateMissionProfile({
                cameraRequirement: val as 'eo' | 'thermal' | 'both',
              })
            }
            delay={400}
          />
        </div>
      </div>
    </div>
  )
}

interface TypeformInputProps {
  icon: React.ReactNode
  label: string
  sublabel: string
  value: number
  onChange: (value: number) => void
  suffix: string
  delay: number
}

function TypeformInput({
  icon,
  label,
  sublabel,
  value,
  onChange,
  suffix,
  delay,
}: TypeformInputProps) {
  const [isFocused, setIsFocused] = useState(false)

  return (
    <div
      className={cn(
        'wizard-stagger p-4 rounded-lg border transition-all duration-200',
        isFocused
          ? 'border-primary/50 bg-primary/5'
          : 'border-border/30 hover:border-border/50'
      )}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start gap-4">
        <div
          className={cn(
            'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors',
            isFocused ? 'bg-primary/20 text-primary' : 'bg-secondary/50 text-muted-foreground'
          )}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <label className="block text-sm font-medium text-foreground mb-1">{label}</label>
          <p className="text-xs text-muted-foreground/60 mb-3">{sublabel}</p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={value || ''}
              onChange={(e) => onChange(Number(e.target.value))}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className={cn(
                'w-32 bg-transparent text-2xl font-semibold text-foreground',
                'border-b-2 transition-colors focus:outline-none',
                isFocused ? 'border-primary' : 'border-border/40'
              )}
              min={0}
            />
            <span className="text-sm text-muted-foreground">{suffix}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

interface TypeformSelectProps {
  icon: React.ReactNode
  label: string
  sublabel: string
  options: { value: string; label: string; description: string }[]
  value: string
  onChange: (value: string) => void
  delay: number
}

function TypeformSelect({
  icon,
  label,
  sublabel,
  options,
  value,
  onChange,
  delay,
}: TypeformSelectProps) {
  return (
    <div
      className="wizard-stagger p-4 rounded-lg border border-border/30 hover:border-border/50 transition-all duration-200"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/50 flex items-center justify-center text-muted-foreground">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <label className="block text-sm font-medium text-foreground mb-1">{label}</label>
          <p className="text-xs text-muted-foreground/60 mb-3">{sublabel}</p>
          <div className="flex flex-wrap gap-2">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => onChange(option.value)}
                className={cn(
                  'px-4 py-2 rounded-md text-sm transition-all',
                  value === option.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

